import type { AABB, PolygonWithHoles, Vec2 } from '../geo/types';
import type { Channel, Layout } from '../data/types';
import type { WaterKind } from './types';
import { closestPointOnPolyline } from '../geo/polyline';
import { pointInPolygon, ringBounds } from '../geo/polygon';
import { difference, fromRing, union } from '../geo/clip';
import { extendChannelEnds, type ChannelExit } from './channelEnds';
import { cappedStrip, jointCaps } from './channelJoints';

export interface ChannelRegion {
  readonly channel: Channel;
  readonly polygons: readonly PolygonWithHoles[];
  readonly bounds: AABB;
  readonly exits: readonly ChannelExit[];
}

export interface WaterClass {
  readonly channel: Channel | null;
  readonly kind: WaterKind;
}

export interface WaterClassifier {
  classify(p: Vec2): WaterClass;
}

// Each strip is resolved on its own first: a loop at a sharp bend is wound against the strip,
// and left raw it would cancel whatever water it overlaps in the shared nonzero union. The dams
// (layout water.dams) are dry ground the strips run over, so no channel claims them.
export function buildChannelRegions(
  channels: readonly Channel[],
  openWater: readonly PolygonWithHoles[] = [],
  bounds: AABB | null = null,
  dams: readonly PolygonWithHoles[] = []
): readonly ChannelRegion[] {
  const caps = jointCaps(channels);
  const paths = extendChannelEnds(channels, openWater, bounds, caps);
  return channels.flatMap((channel, i) => {
    const ring = cappedStrip(paths[i]!, caps[i]!);
    const strip = union([fromRing(ring)], 'nonzero');
    const polygons = dams.length > 0 ? difference(strip, dams) : strip;
    const exits = paths[i]!.exits;
    return polygons.length > 0 ? [{ channel, polygons, bounds: ringBounds(ring), exits }] : [];
  });
}

function inBounds(p: Vec2, box: AABB): boolean {
  return p[0] >= box.minX && p[0] <= box.maxX && p[1] >= box.minY && p[1] <= box.maxY;
}

// Strips overlap at junctions and mouths; the channel whose centerline is nearest owns the point.
function channelContaining(p: Vec2, regions: readonly ChannelRegion[]): Channel | null {
  let best: Channel | null = null;
  let bestDistance = Infinity;
  for (const region of regions) {
    if (!inBounds(p, region.bounds)) continue;
    if (!region.polygons.some((polygon) => pointInPolygon(p, polygon))) continue;
    const distance = closestPointOnPolyline(region.channel.centerline, p).distance;
    if (distance < bestDistance) {
      best = region.channel;
      bestDistance = distance;
    }
  }
  return best;
}

// PLAN §5 step 6, amended: area > channel strip kind > ij, and no strip claims the IJ. A channel
// ends where it enters open water; its strip reaches on into that water (§5 step 1) only to close
// the land at its mouth, so the water there stays the area's or the IJ's.
export function createWaterClassifier(
  layout: Layout,
  regions: readonly ChannelRegion[]
): WaterClassifier {
  const areas = layout.water.areas.map(({ polygon }) => ({
    polygon,
    bounds: ringBounds(polygon.outer),
  }));
  const inArea = (p: Vec2) =>
    areas.some(({ polygon, bounds }) => inBounds(p, bounds) && pointInPolygon(p, polygon));

  return {
    classify(p: Vec2): WaterClass {
      if (inArea(p)) return { channel: null, kind: 'area' };
      const channel = channelContaining(p, regions);
      if (channel && !pointInPolygon(p, layout.water.ij)) return { channel, kind: channel.kind };
      return { channel: null, kind: 'ij' };
    },
  };
}
